const MERCATOR_LATITUDE_LIMIT = 85.05112878;

const coordinate = (value, limit) => {
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    if (typeof value === 'string' && !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim())) return null;
    const number = Number(value);
    return Number.isFinite(number) && Math.abs(number) <= limit ? number : null;
};

export const venueMapCoordinates = (latitude, longitude) => {
    const lat = coordinate(latitude, MERCATOR_LATITUDE_LIMIT);
    const lon = coordinate(longitude, 180);
    return lat !== null && lon !== null ? [lat, lon] : null;
};

const instances = new WeakMap();
const active = new Set();
let libraryPromise;

const loadLibrary = () => {
    if (!libraryPromise) {
        libraryPromise = import('leaflet').then(module => module.default ?? module).catch(error => {
            libraryPromise = null;
            throw error;
        });
    }
    return libraryPromise;
};

const initialize = details => {
    if (instances.has(details)) return;
    const container = details.querySelector('[data-venue-map-surface]');
    const status = details.querySelector('[data-venue-map-status]');
    const retry = details.querySelector('[data-venue-map-retry]');
    const center = details.querySelector('[data-venue-map-center]');
    const iconTemplate = details.querySelector('[data-venue-map-icon]');
    if (!container || !status || !retry || !center || !iconTemplate) return;
    const controller = new AbortController();
    const state = { details, container, status, retry, center, controller, map: null, tiles: null, pending: false, disposed: false, failed: false, frame: null };
    instances.set(details, state);
    active.add(state);
    details.hidden = false;

    const feedback = (message, failed = false) => {
        if (state.disposed) return;
        status.textContent = message;
        retry.hidden = !failed;
        container.setAttribute('aria-busy', 'false');
    };
    const resize = () => {
        if (state.frame !== null) cancelAnimationFrame(state.frame);
        state.frame = requestAnimationFrame(() => {
            state.frame = null;
            if (!state.disposed && details.open) state.map?.invalidateSize({ animate: false });
        });
    };
    const open = async () => {
        if (state.disposed || state.pending || !details.open || !details.isConnected) return;
        if (state.map) {
            resize();
            return;
        }
        const point = venueMapCoordinates(details.dataset.latitude, details.dataset.longitude);
        if (!point) {
            feedback('Venue coordinates are unavailable.');
            return;
        }
        state.pending = true;
        feedback('Loading venue map.');
        container.setAttribute('aria-busy', 'true');
        try {
            const L = await loadLibrary();
            if (state.disposed || !details.isConnected || !details.open) return;
            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            state.map = L.map(container, {
                scrollWheelZoom: false, keyboard: true, zoomControl: true,
                zoomAnimation: !reduced, fadeAnimation: !reduced, markerZoomAnimation: !reduced,
            }).setView(point, 16);
            state.tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" rel="noopener noreferrer external">OpenStreetMap contributors</a>',
            });
            state.tiles.on('tileerror', () => {
                state.failed = true;
                feedback('Map tiles could not load.', true);
            });
            state.tiles.on('load', () => {
                if (!state.failed) feedback('Venue map loaded.');
            });
            state.tiles.addTo(state.map);
            const label = details.dataset.venueLabel || 'Concert venue';
            const popup = document.createElement('span');
            popup.textContent = label;
            const iconNode = iconTemplate.content.firstElementChild.cloneNode(true);
            const icon = L.divIcon({ html: iconNode, className: 'ff-venue-map-marker', iconSize: [40, 48], iconAnchor: [20, 42], popupAnchor: [0, -40] });
            L.marker(point, { icon, keyboard: true, title: label, alt: label }).addTo(state.map)
                .bindPopup(popup, { maxWidth: Math.min(280, Math.max(120, container.clientWidth - 80)), autoPanPadding: [24, 24] });
            details.dataset.venueMapReady = 'true';
            center.hidden = false;
            resize();
        } catch {
            state.map?.remove();
            state.map = null;
            state.tiles = null;
            feedback('Venue map could not load.', true);
        } finally {
            state.pending = false;
        }
    };
    details.addEventListener('toggle', () => { if (details.open) void open(); }, { signal: controller.signal });
    retry.addEventListener('click', () => {
        if (state.disposed) return;
        state.failed = false;
        if (state.tiles) {
            feedback('Loading venue map.');
            container.setAttribute('aria-busy', 'true');
            state.tiles.redraw();
        } else void open();
    }, { signal: controller.signal });
    center.addEventListener('click', () => {
        const point = venueMapCoordinates(details.dataset.latitude, details.dataset.longitude);
        if (!state.disposed && state.map && point) state.map.setView(point, 16, { animate: false });
    }, { signal: controller.signal });
    if (details.open) void open();
};

const initializeAll = () => document.querySelectorAll('[data-concert-venue-map]').forEach(initialize);
const cleanup = () => {
    for (const state of active) {
        state.disposed = true;
        state.controller.abort();
        if (state.frame !== null) cancelAnimationFrame(state.frame);
        state.map?.remove();
        state.container.replaceChildren();
        state.container.setAttribute('aria-busy', 'false');
        state.status.textContent = '';
        state.retry.hidden = true;
        state.center.hidden = true;
        state.details.hidden = true;
        delete state.details.dataset.venueMapReady;
        instances.delete(state.details);
    }
    active.clear();
};

if (typeof document !== 'undefined') {
    document.addEventListener('livewire:navigating', cleanup);
    document.addEventListener('livewire:navigated', initializeAll);
    window.addEventListener('pageshow', initializeAll);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
    else initializeAll();
}
